import { get } from 'lodash'
import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import useDebounce from 'utils/useDebounce'
import { getEncodedOccurrence, getOrganizationId, getTimesheetMode, getVolunteers } from 'kiosk/selectors'
import { reportSearchMiss } from '../kioskDiagnostics'

// Long enough that we only report a query the operator stopped at and was left
// staring at an empty list, instead of one event per keystroke on the way there.
const SETTLED_MS = 1500

const NAME_PATHS = ['user.givenName', 'user.familyName']

// Deliberately a plain name-prefix test rather than a second MiniSearch index: it
// only has to reproduce what the support reports describe - someone typed the start
// of their name and saw nothing - and it runs only once a search has already missed.
// Names in the roster sometimes carry trailing whitespace, hence the trim.
const nameStartsWith = (volunteer, needle) =>
  NAME_PATHS.some(path => {
    const value = get(volunteer, path)
    return typeof value === 'string' && value.trim().toLowerCase().startsWith(needle)
  })

const isCheckedIn = volunteer => volunteer.checkedInAt != null

// Check-in lists volunteers without an open session, check-out lists the ones with
// one, so each mode hides exactly the people the other one shows.
const belongsToMode = (volunteer, mode) => (mode === 'check-in' ? !isCheckedIn(volunteer) : isCheckedIn(volunteer))

export default function useKioskSearchDiagnostics({ query, search }) {
  const roster = useSelector(getVolunteers)
  const mode = useSelector(getTimesheetMode)
  const organizationId = useSelector(getOrganizationId)
  const encodedOccurrence = useSelector(getEncodedOccurrence)

  const settledQuery = useDebounce(query, SETTLED_MS)
  const reportedKey = useRef(null)

  useEffect(() => {
    const needle = (settledQuery || '').trim().toLowerCase()
    if (!needle) return

    // Nothing in here may take a kiosk down, so the whole block is best-effort.
    try {
      // One event per query and mode, so a roster poll rebuilding the search index
      // does not report the same miss again.
      const key = `${mode}:${needle}`
      if (reportedKey.current === key) return

      // Running the real search keeps the event honest about what the operator saw,
      // and only happens after typing settles rather than on every keystroke.
      if (search(settledQuery).length > 0) return

      reportedKey.current = key

      reportSearchMiss({
        organizationId,
        encodedOccurrence,
        mode,
        queryLength: needle.length,
        rosterCount: roster.length,
        candidatesInMode: roster.filter(volunteer => belongsToMode(volunteer, mode)).length,
        // The whole point of the event: a miss here that would have matched on the
        // other tab means the volunteer was findable all along and the mode was wrong.
        nameMatchesInOtherMode: roster.filter(
          volunteer => !belongsToMode(volunteer, mode) && nameStartsWith(volunteer, needle),
        ).length,
      })
    } catch (error) {
      // Diagnostics are never worth interrupting a check-in for.
    }
  }, [settledQuery, search, mode, roster, organizationId, encodedOccurrence])
}
